// src/chatGPT/components/ImprovedMarkdownRenderer.jsx
import React, { useMemo, useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Copy } from "lucide-react";
import clsx from "clsx";
import { cleanMarkdown } from "../utils/markdownParser";
import { copyForWord } from "../../utils/copyForWord";
// Add these imports at the top
import EnrollmentTrendChart from "../../components/EnrollmentTrendChart";
import ProgramComparisonChart from "../../components/ProgramComparisonChart";
// Import your custom components
import EquityNivoBarChart from "../../components/EquityNivoBarChart";
import JobsTable from "../../components/JobsTable";
import FlexibleBarChart from "../../components/FlexibleBarChart";
import FlexibleLineChart from "../../components/FlexibleLineChart";
import { CipsElement } from "../../components/CipsElement";
import LaborMarketTable from "./LaborMarketTable";

/**
 * Strip emoji characters from text
 */
function stripEmojis(text) {
  if (!text) return text;
  // Remove emoji characters (covers most common emoji ranges)
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]/gu, '').trim();
}

/**
 * Parse artifacts from content
 */
function parseArtifacts(content) {
  const artifactPattern = /<artifact\s+identifier="([^"]+)"\s+type="([^"]+)"(?:\s+title="([^"]+)")?(?:\s+data='([^']+)')?>([\s\S]*?)<\/artifact>/g;
  const artifacts = [];
  let lastIndex = 0;
  let regularContent = '';

  let match;
  while ((match = artifactPattern.exec(content)) !== null) {
    regularContent += content.slice(lastIndex, match.index);
    
    let data = null;
    if (match[4]) {
      try {
        data = JSON.parse(match[4]);
      } catch (e) {
        console.error('Failed to parse artifact data:', e);
      }
    }
    
    artifacts.push({
      identifier: match[1],
      type: match[2],
      title: stripEmojis(match[3]) || 'Untitled',
      data: data,
      content: match[5].trim(),
      index: artifacts.length
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  regularContent += content.slice(lastIndex);
  
  return { regularContent, artifacts };
}

/**
 * Artifact Renderer Component
 */

function ArtifactRenderer({ artifact }) {
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);
  const { type, title, content } = artifact;

  // Parse JSON content safely
  let data = {};
  try {
    data = JSON.parse(content);
  } catch (e) {
    console.error('Failed to parse artifact data:', e);
    return (
      <div className="border border-red-200 rounded-lg p-4 my-6 bg-red-50">
        <h3 className="font-semibold text-red-900 mb-2">{title}</h3>
        <p className="text-red-700 text-sm">Error parsing data</p>
        <pre className="text-xs bg-white p-2 rounded mt-2 overflow-x-auto">{content}</pre>
      </div>
    );
  }

  // Handle copy functionality (Word-friendly HTML)
  const handleCopy = async () => {
    if (!contentRef.current) return;
    const success = await copyForWord(contentRef.current, title);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Artifact header component
  const ArtifactHeader = ({ bgColor, textColor, children, showCopy = true }) => (
    <div className={`${bgColor} px-4 py-3 flex items-center justify-between`}>
      <div className="flex items-center gap-2">
        <h3 className={`font-semibold ${textColor}`}>{title}</h3>
        {children}
      </div>
      {showCopy && (
        <button
          onClick={handleCopy}
          className={`${textColor} hover:opacity-70 transition-opacity`}
          title="Copy"
        >
          {copied ? '✓' : <Copy size={16} />}
        </button>
      )}
    </div>
  );

  // Artifact footer component
  const ArtifactFooter = ({ source = "CTElink Assistant", updated = new Date().toLocaleDateString() }) => (
    <div className="bg-gray-50 px-4 py-2 text-xs text-gray-500 border-t">
      Source: {source} • Updated: {updated} • ID: {artifact.identifier}
    </div>
  );

  switch (type) {
    case 'chart/bar':
      return (
        <div className="border-2 border-blue-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader bgColor="bg-gradient-to-r from-blue-50 to-indigo-50" textColor="text-blue-900" showCopy={false} />
          <div ref={contentRef} className="bg-white p-4">
            <FlexibleBarChart {...data} title={title} />
          </div>
          <ArtifactFooter source="Washington State CTE Data" />
        </div>
      );

    case 'chart/line':
      return (
        <div className="border border-stone-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader bgColor="bg-gradient-to-r from-stone-50 to-stone-100" textColor="text-stone-800" showCopy={false} />
          <div ref={contentRef} className="bg-white p-4">
            <FlexibleLineChart {...data} title={title} />
          </div>
          <ArtifactFooter source="Washington State CTE Data" />
        </div>
      );

    case 'chart/equity':
      return (
        <div className="border border-stone-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader bgColor="bg-gradient-to-r from-stone-50 to-stone-100" textColor="text-stone-800" showCopy={false} />
          <div ref={contentRef} className="bg-white p-4">
            <EquityNivoBarChart {...data} />
          </div>
          <ArtifactFooter source="Washington State Equity Analysis" />
        </div>
      );

    case 'table/jobs':
    case 'table/wa-labor-market':
      return (
        <div className="border-2 border-purple-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader
            bgColor="bg-gradient-to-r from-purple-50 to-pink-50"
            textColor="text-purple-900"
          >
            {(data?.jobs?.length || data?.socCodes?.length) && (
              <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded ml-2">
                {data.jobs?.length || data.socCodes?.length} career paths
              </span>
            )}
          </ArtifactHeader>
          <div ref={contentRef} className="bg-white p-4">
            <LaborMarketTable
              jobs={data.jobs}
              socCodes={data.socCodes}
              title={data.tableTitle}
              subtitle={data.tableSubtitle || data.subtitle}
              showExport={data.showExport !== false}
            />
          </div>
          <ArtifactFooter source="Washington State Labor Market Data" />
        </div>
      );

    case 'table/data':
      return (
        <div className="border-2 border-gray-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader bgColor="bg-gradient-to-r from-gray-50 to-slate-50" textColor="text-gray-900" />
          <div ref={contentRef} className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {data.headers?.map((header, i) => (
                    <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.rows?.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {row.map((cell, j) => (
                      <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ArtifactFooter />
        </div>
      );

    default:
      return (
        <div className="border-2 border-amber-200 rounded-lg overflow-hidden my-6 shadow-sm">
          <ArtifactHeader bgColor="bg-gradient-to-r from-amber-50 to-yellow-50" textColor="text-amber-900" />
          <div ref={contentRef} className="bg-white p-4">
            <pre className="whitespace-pre-wrap text-sm overflow-x-auto">{content}</pre>
          </div>
          <ArtifactFooter />
        </div>
      );
  }
}








export default function ImprovedMarkdownRenderer({ children, context = {} }) {
  const rawContent = useMemo(() => {
    const raw =
      typeof children === "string"
        ? children
        : React.Children.toArray(children).join("");

    if (!raw) return "";
    return raw;  
  }, [children]);

  // Parse artifacts and clean markdown
  const { regularContent, artifacts } = useMemo(() => {
    const cleaned = cleanMarkdown(rawContent);
    return parseArtifacts(cleaned);
  }, [rawContent]);

  return (
    <div className="markdown-content text-gray-800 max-w-full">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          // Main headings (centered within wider container)
          h1: ({ children }) => (
            <h1 className="text-3xl font-bold text-gray-900 mb-6 mt-8 first:mt-0
                         border-b border-gray-200 pb-2 max-w-3xl mx-auto">
              {children}
            </h1>
          ),

          // Section headings with subtle accent (centered)
          h2: ({ children }) => {
            const text = React.Children.toArray(children).join("");
            const isTLDR = text.toLowerCase().includes("tl;dr");

            if (isTLDR) {
              return (
                <div className="bg-gradient-to-r from-stone-50 to-stone-100
                              border border-stone-200 rounded-lg p-4 mb-6 mt-6 max-w-3xl mx-auto">
                  <h2 className="text-xl font-semibold text-stone-800">
                    {children}
                  </h2>
                </div>
              );
            }

            return (
              <h2 className="text-2xl font-semibold text-gray-900 mb-4 mt-8 max-w-3xl mx-auto">
                {children}
              </h2>
            );
          },

          // Subsection headings (centered)
          h3: ({ children }) => (
            <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-6 max-w-3xl mx-auto">
              {children}
            </h3>
          ),

          // Smaller headings (centered)
          h4: ({ children }) => (
            <h4 className="text-base font-medium text-gray-700 mb-2 mt-4 max-w-3xl mx-auto">
              {children}
            </h4>
          ),

          // Paragraphs (centered for readability)
          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-gray-700 last:mb-0 max-w-3xl mx-auto">
              {children}
            </p>
          ),
          
          // Emphasis styles
          em: ({ children }) => {
            const text = children?.toString() || '';
            if (text.includes("Initializing")) {
              return (
                <span className="italic text-violet-500 bg-violet-50/50 px-1 rounded">
                  {children}
                </span>
              );
            }
            return (
              <em className="italic text-stone-600 bg-stone-50 px-1 rounded">
                {children}
              </em>
            );
          },
          
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">
              {children}
            </strong>
          ),
          
          // Lists (centered for readability)
          ul: ({ children }) => (
            <ul className="mb-4 space-y-2 list-disc pl-6 max-w-3xl mx-auto">
              {children}
            </ul>
          ),

          ol: ({ children }) => (
            <ol className="mb-4 space-y-2 list-decimal pl-6 max-w-3xl mx-auto">
              {children}
            </ol>
          ),

          li: ({ children }) => (
            <li className="text-gray-700 leading-relaxed">
              {children}
            </li>
          ),
          
          // Links with subtle animation
          a: ({ href, children }) => (
            <a href={href}
               className="text-stone-600 hover:text-stone-800
                        underline decoration-stone-300 hover:decoration-stone-400
                        transition-all duration-200 font-medium"
               target="_blank"
               rel="noopener noreferrer">
              {children}
            </a>
          ),
          
          // Blockquotes with smart styling
          blockquote: ({ children }) => {
            const text = React.Children.toArray(children).join("");
            let styles = {
              container: "border-l-4 rounded-r-lg p-4 mb-4 bg-gradient-to-r",
              border: "border-gray-300",
              gradient: "from-gray-50 to-gray-50",
              text: "text-gray-700"
            };
            
            if (text.toLowerCase().includes("warning") || text.toLowerCase().includes("⚠")) {
              styles = {
                container: "border-l-4 rounded-r-lg p-4 mb-4 bg-gradient-to-r",
                border: "border-amber-400",
                gradient: "from-amber-50 to-orange-50",
                text: "text-amber-900"
              };
            } else if (text.toLowerCase().includes("tip") || text.toLowerCase().includes("💡")) {
              styles = {
                container: "border-l-4 rounded-r-lg p-4 mb-4 bg-gradient-to-r",
                border: "border-violet-300",
                gradient: "from-violet-50/50 to-stone-50",
                text: "text-stone-800"
              };
            } else if (text.toLowerCase().includes("note") || text.toLowerCase().includes("📝")) {
              styles = {
                container: "border-l-4 rounded-r-lg p-4 mb-4 bg-gradient-to-r",
                border: "border-blue-400",
                gradient: "from-blue-50 to-indigo-50",
                text: "text-blue-900"
              };
            }
            
            return (
              <blockquote className={`${styles.container} ${styles.border} ${styles.gradient} max-w-3xl mx-auto`}>
                <div className={styles.text}>
                  {children}
                </div>
              </blockquote>
            );
          },
          
          // Code styling
          code: ({ inline, className, children }) => {
            const match = /language-(\w+)/.exec(className || '');
            
            if (!inline && match) {
              return (
                <div className="relative mb-4 group">
                  <div className="absolute top-0 right-0 px-2 py-1 text-xs 
                                text-gray-500 bg-gray-100 rounded-bl-md 
                                font-mono opacity-0 group-hover:opacity-100 
                                transition-opacity duration-200">
                    {match[1]}
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg 
                                overflow-x-auto shadow-inner">
                    <code className={`language-${match[1]} text-sm font-mono`}>
                      {children}
                    </code>
                  </pre>
                </div>
              );
            }
            
            return (
              <code className="bg-stone-100 text-stone-700 px-1.5 py-0.5
                             rounded text-sm font-mono">
                {children}
              </code>
            );
          },
          
          // Tables with modern design
          table: ({ children }) => (
            <div className="mb-6 overflow-hidden rounded-lg shadow-sm
                          border border-gray-200 max-w-full">
              <div className="overflow-x-auto max-w-full">
                <table className="min-w-full divide-y divide-gray-200">
                  {children}
                </table>
              </div>
            </div>
          ),
          
          thead: ({ children }) => (
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              {children}
            </thead>
          ),
          
          tbody: ({ children }) => (
            <tbody className="bg-white divide-y divide-gray-200">
              {children}
            </tbody>
          ),
          
          tr: ({ children }) => (
            <tr className="hover:bg-gray-50 transition-colors duration-150">
              {children}
            </tr>
          ),
          
          th: ({ children }) => (
            <th className="px-3 py-2.5 text-left text-xs font-medium
                         text-gray-600 uppercase tracking-wide">
              {children}
            </th>
          ),

          td: ({ children }) => (
            <td className="px-3 py-2.5 text-sm text-gray-700 tracking-tight">
              {children}
            </td>
          ),
          
          // Horizontal rules
          hr: () => (
            <hr className="my-8 border-t border-gray-200" />
          ),
        }}
      >
        {regularContent}
      </ReactMarkdown>
      
      {/* Render artifacts */}
      {artifacts.map((artifact, idx) => (
        <ArtifactRenderer key={idx} artifact={artifact} context={context} />
      ))}
      
      <style jsx>{`
        .markdown-content {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI",
                       Roboto, "Helvetica Neue", Arial, sans-serif;
          line-height: 1.6;
          color: #1a202c;
        }
      `}</style>
    </div>
  );
}